/**
 * Postcodes lookup data for the Cambourne area
 * Used for address input validation and auto-completion
 * 
 * Key: Normalized postcode (no spaces, uppercase) e.g., "CB235AB"
 * Value: PostcodeEntry with area, zone, description (street), and formatted postcode
 */

export interface PostcodeEntry {
    area: string;           // e.g., "Great Cambourne", "Upper Cambourne", "Lower Cambourne"
    zone: number;           // Delivery zone: 10, 20, 30
    description: string;    // Street name, e.g., "Reans Meadow"
    postcode: string;       // Formatted postcode, e.g., "CB23 5AB"
}

export interface PostcodeLookupResult {
    found: boolean;
    entry?: PostcodeEntry;
    normalizedKey?: string;
}

/**
 * Normalize a postcode input for lookup
 * Removes spaces and converts to uppercase
 */
export function normalizePostcode(input: string): string {
    return input.replace(/\s+/g, '').toUpperCase();
}

/**
 * Check if a postcode input has enough characters for lookup (7 non-space chars)
 */
export function isPostcodeComplete(input: string): boolean {
    const normalized = normalizePostcode(input);
    return normalized.length >= 7;
}

/**
 * Look up a postcode in the database
 * Returns the entry if found, or a not-found result
 */
export function lookupPostcode(input: string): PostcodeLookupResult {
    const normalizedKey = normalizePostcode(input);
    
    if (!isPostcodeComplete(input)) {
        return { found: false };
    }
    
    const entry = POSTCODES[normalizedKey];
    
    if (entry) {
        return {
            found: true,
            entry,
            normalizedKey
        };
    }
    
    return { found: false, normalizedKey };
}

/**
 * Postcodes database
 * Source: public/files/data/postcodes.json
 */
export const POSTCODES: Record<string, PostcodeEntry> = {
    "CB234LA": {
        "area": "Great Cambourne",
        "zone": 20,
        "description": "Elsworth",
        "postcode": "CB23 4LA"
    },
    "CB235AB": {
        "area": "Great Cambourne",
        "zone": 20,
        "description": "Reans Meadow",
        "postcode": "CB23 5AB"
    },
    "CB235AD": {
        "area": "Great Cambourne",
        "zone": 20,
        "description": "Fenbridge",
        "postcode": "CB23 5AD"
    },
    "CB235AE": {
        "area": "Great Cambourne",
        "zone": 20,
        "description": "Langate Green",
        "postcode": "CB23 5AE"
    },
    "CB235AF": {
        "area": "Great Cambourne",
        "zone": 20,
        "description": "Playcross Close",
        "postcode": "CB23 5AF"
    },
    "CB235AP": {
        "area": "Great Cambourne",
        "zone": 20,
        "description": "Crow Hill Lane",
        "postcode": "CB23 5AP"
    },
    "CB235AR": {
        "area": "Great Cambourne",
        "zone": 20,
        "description": "Saunders Court",
        "postcode": "CB23 5AR"
    },
    "CB235AS": {
        "area": "Great Cambourne",
        "zone": 20,
        "description": "Douglas Way",
        "postcode": "CB23 5AS"
    },
    "CB235AT": {
        "area": "Great Cambourne",
        "zone": 20,
        "description": "Kemmann Lane",
        "postcode": "CB23 5AT"
    },
    "CB235AU": {
        "area": "Great Cambourne",
        "zone": 20,
        "description": "Kemmann Lane",
        "postcode": "CB23 5AU"
    },
    "CB235AW": {
        "area": "Great Cambourne",
        "zone": 20,
        "description": "Crow Hill Lane",
        "postcode": "CB23 5AW"
    },
    "CB235AX": {
        "area": "Great Cambourne",
        "zone": 20,
        "description": "Monkfield Park Primary School",
        "postcode": "CB23 5AX"
    },
    "CB235BH": {
        "area": "Great Cambourne",
        "zone": 20,
        "description": "Greenhaze Lane",
        "postcode": "CB23 5BH"
    },
    "CB235BL": {
        "area": "Great Cambourne",
        "zone": 20,
        "description": "Priory Walk",
        "postcode": "CB23 5BL"
    },
    "CB235BN": {
        "area": "Lower Cambourne",
        "zone": 30,
        "description": "Orchard Way",
        "postcode": "CB23 5BN"
    },
    "CB235BP": {
        "area": "Great Cambourne",
        "zone": 20,
        "description": "Foster Way",
        "postcode": "CB23 5BP"
    },
    "CB235BQ": {
        "area": "Great Cambourne",
        "zone": 20,
        "description": "Granary Way",
        "postcode": "CB23 5BQ"
    },
    "CB235DG": {
        "area": "Lower Cambourne",
        "zone": 30,
        "description": "School Lane",
        "postcode": "CB23 5DG"
    },
    "CB235DJ": {
        "area": "Lower Cambourne",
        "zone": 30,
        "description": "School Lane",
        "postcode": "CB23 5DJ"
    },
    "CB235DQ": {
        "area": "Lower Cambourne",
        "zone": 30,
        "description": "School Lane",
        "postcode": "CB23 5DQ"
    },
    "CB235DR": {
        "area": "Lower Cambourne",
        "zone": 30,
        "description": "School Lane",
        "postcode": "CB23 5DR"
    },
    "CB235DT": {
        "area": "Great Cambourne",
        "zone": 20,
        "description": "Wether Road",
        "postcode": "CB23 5DT"
    },
    "CB235DU": {
        "area": "Great Cambourne",
        "zone": 20,
        "description": "Stagwell Road",
        "postcode": "CB23 5DU"
    },
    "CB235DX": {
        "area": "Lower Cambourne",
        "zone": 30,
        "description": "School Lane",
        "postcode": "CB23 5DX"
    },
    "CB235DY": {
        "area": "Great Cambourne",
        "zone": 20,
        "description": "Sitch Road",
        "postcode": "CB23 5DY"
    },
    "CB235DZ": {
        "area": "Lower Cambourne",
        "zone": 30,
        "description": "School Lane",
        "postcode": "CB23 5DZ"
    },
    "CB235EB": {
        "area": "Lower Cambourne",
        "zone": 30,
        "description": "Brookfield Way",
        "postcode": "CB23 5EB"
    },
    "CB235ED": {
        "area": "Lower Cambourne",
        "zone": 30,
        "description": "Brookfield Way",
        "postcode": "CB23 5ED"
    },
    "CB235EE": {
        "area": "Lower Cambourne",
        "zone": 30,
        "description": "Pyrecroft",
        "postcode": "CB23 5EE"
    },
    "CB235EF": {
        "area": "Great Cambourne",
        "zone": 20,
        "description": "Greenhaze Lane",
        "postcode": "CB23 5EF"
    },
    "CB235EG": {
        "area": "Great Cambourne",
        "zone": 20,
        "description": "Stable Yard",
        "postcode": "CB23 5EG"
    },
    "CB235EH": {
        "area": "Great Cambourne",
        "zone": 20,
        "description": "The Paddock",
        "postcode": "CB23 5EH"
    },
    "CB235EJ": {
        "area": "Lower Cambourne",
        "zone": 30,
        "description": "Damson Way",
        "postcode": "CB23 5EJ"
    },
    "CB235EN": {
        "area": "Lower Cambourne",
        "zone": 30,
        "description": "Wellingley Court",
        "postcode": "CB23 5EN"
    },
    "CB235EP": {
        "area": "Lower Cambourne",
        "zone": 30,
        "description": "Bickerley Court",
        "postcode": "CB23 5EP"
    },
    "CB235EQ": {
        "area": "Lower Cambourne",
        "zone": 30,
        "description": "Watermead Crescent",
        "postcode": "CB23 5EQ"
    },
    "CB235FA": {
        "area": "Great Cambourne",
        "zone": 20,
        "description": "Jeavons Lane",
        "postcode": "CB23 5FA"
    },
    "CB235FJ": {
        "area": "Great Cambourne",
        "zone": 20,
        "description": "Miller Way",
        "postcode": "CB23 5FJ"
    },
    "CB235FL": {
        "area": "Great Cambourne",
        "zone": 20,
        "description": "Millhouse Walk",
        "postcode": "CB23 5FL"
    },
    "CB235FN": {
        "area": "Great Cambourne",
        "zone": 20,
        "description": "Jeavons Lane",
        "postcode": "CB23 5FN"
    },
    "CB235FP": {
        "area": "Great Cambourne",
        "zone": 20,
        "description": "Plowsters Lane",
        "postcode": "CB23 5FP"
    },
    "CB235FR": {
        "area": "Great Cambourne",
        "zone": 20,
        "description": "The Maltings",
        "postcode": "CB23 5FR"
    },
    "CB235FS": {
        "area": "Great Cambourne",
        "zone": 20,
        "description": "Salgasson Walk",
        "postcode": "CB23 5FS"
    },
    "CB235FW": {
        "area": "Lower Cambourne",
        "zone": 30,
        "description": "Hop Gardens",
        "postcode": "CB23 5FW"
    },
    "CB235FX": {
        "area": "Lower Cambourne",
        "zone": 30,
        "description": "School Lane",
        "postcode": "CB23 5FX"
    },
    "CB235HS": {
        "area": "Great Cambourne",
        "zone": 20,
        "description": "Honeysuckle Close",
        "postcode": "CB23 5HS"
    },
    "CB235HT": {
        "area": "Great Cambourne",
        "zone": 20,
        "description": "Foxhollow",
        "postcode": "CB23 5HT"
    },
    "CB235HU": {
        "area": "Great Cambourne",
        "zone": 20,
        "description": "Fawnlea",
        "postcode": "CB23 5HU"
    },
    "CB235HW": {
        "area": "Great Cambourne",
        "zone": 20,
        "description": "Foxhollow",
        "postcode": "CB23 5HW"
    },
    "CB235HX": {
        "area": "Great Cambourne",
        "zone": 20,
        "description": "Horsecroft Drive",
        "postcode": "CB23 5HX"
    },
    "CB235HY": {
        "area": "Great Cambourne",
        "zone": 20,
        "description": "Yaffle Mews",
        "postcode": "CB23 5HY"
    },
    "CB235HZ": {
        "area": "Great Cambourne",
        "zone": 20,
        "description": "Garstones",
        "postcode": "CB23 5HZ"
    },
    "CB235JA": {
        "area": "Great Cambourne",
        "zone": 20,
        "description": "Mayfield Way",
        "postcode": "CB23 5JA"
    },
    "CB235JB": {
        "area": "Great Cambourne",
        "zone": 20,
        "description": "Cooper Road",
        "postcode": "CB23 5JB"
    },
    "CB235JD": {
        "area": "Great Cambourne",
        "zone": 20,
        "description": "Laith Road",
        "postcode": "CB23 5JD"
    },
    "CB235JE": {
        "area": "Great Cambourne",
        "zone": 20,
        "description": "Jeavons Lane",
        "postcode": "CB23 5JE"
    },
    "CB235JF": {
        "area": "Great Cambourne",
        "zone": 20,
        "description": "Lamb Drove",
        "postcode": "CB23 5JF"
    },
    "CB235JG": {
        "area": "Great Cambourne",
        "zone": 20,
        "description": "Shepard Close",
        "postcode": "CB23 5JG"
    },
    "CB235JH": {
        "area": "Great Cambourne",
        "zone": 20,
        "description": "Jeavons Lane",
        "postcode": "CB23 5JH"
    },
    "CB235JJ": {
        "area": "Great Cambourne",
        "zone": 20,
        "description": "Mayfield Way",
        "postcode": "CB23 5JJ"
    },
    "CB235JL": {
        "area": "Great Cambourne",
        "zone": 20,
        "description": "Springfield Close",
        "postcode": "CB23 5JL"
    },
    "CB235JQ": {
        "area": "Great Cambourne",
        "zone": 20,
        "description": "Greenbank",
        "postcode": "CB23 5JQ"
    },
    "CB235JR": {
        "area": "Great Cambourne",
        "zone": 20,
        "description": "Moss-Nook",
        "postcode": "CB23 5JR"
    },
    "CB235JS": {
        "area": "Great Cambourne",
        "zone": 20,
        "description": "Oakwood Drive",
        "postcode": "CB23 5JS"
    },
    "CB235JT": {
        "area": "Great Cambourne",
        "zone": 20,
        "description": "Friar Way",
        "postcode": "CB23 5JT"
    },
    "CB235JU": {
        "area": "Great Cambourne",
        "zone": 20,
        "description": "Bucksherd Close",
        "postcode": "CB23 5JU"
    },
    "CB236AA": {
        "area": "Great Cambourne",
        "zone": 20,
        "description": "Willow Lane",
        "postcode": "CB23 6AA"
    },
    "CB236AB": {
        "area": "Great Cambourne",
        "zone": 20,
        "description": "Willow Lane",
        "postcode": "CB23 6AB"
    },
    "CB236AD": {
        "area": "Great Cambourne",
        "zone": 20,
        "description": "Chapmans Drive",
        "postcode": "CB23 6AD"
    },
    "CB236AE": {
        "area": "Great Cambourne",
        "zone": 20,
        "description": "Tithe Way",
        "postcode": "CB23 6AE"
    },
    "CB236AF": {
        "area": "Great Cambourne",
        "zone": 20,
        "description": "Jeavons Lane",
        "postcode": "CB23 6AF"
    },
    "CB236AG": {
        "area": "Great Cambourne",
        "zone": 20,
        "description": "Jeavons Lane",
        "postcode": "CB23 6AG"
    },
    "CB236AH": {
        "area": "Great Cambourne",
        "zone": 20,
        "description": "Monkfield Lane",
        "postcode": "CB23 6AH"
    },
    "CB236AJ": {
        "area": "Great Cambourne",
        "zone": 20,
        "description": "Monkfield Lane",
        "postcode": "CB23 6AJ"
    },
    "CB236AL": {
        "area": "Great Cambourne",
        "zone": 20,
        "description": "Maple Close",
        "postcode": "CB23 6AL"
    },
    "CB236AN": {
        "area": "Great Cambourne",
        "zone": 20,
        "description": "Elm Tree Close",
        "postcode": "CB23 6AN"
    },
    "CB236AP": {
        "area": "Upper Cambourne",
        "zone": 10,
        "description": "Blenheim Close",
        "postcode": "CB23 6AP"
    },
    "CB236AQ": {
        "area": "Great Cambourne",
        "zone": 20,
        "description": "Alder Drive",
        "postcode": "CB23 6AQ"
    },
    "CB236AR": {
        "area": "Upper Cambourne",
        "zone": 10,
        "description": "Sterling Way",
        "postcode": "CB23 6AR"
    },
    "CB236AS": {
        "area": "Upper Cambourne",
        "zone": 10,
        "description": "Whitley Road",
        "postcode": "CB23 6AS"
    },
    "CB236AT": {
        "area": "Upper Cambourne",
        "zone": 10,
        "description": "Lancaster Gate",
        "postcode": "CB23 6AT"
    },
    "CB236AU": {
        "area": "Upper Cambourne",
        "zone": 10,
        "description": "Lancaster Gate",
        "postcode": "CB23 6AU"
    },
    "CB236AW": {
        "area": "Upper Cambourne",
        "zone": 10,
        "description": "Hampden Close",
        "postcode": "CB23 6AW"
    },
    "CB236AX": {
        "area": "Upper Cambourne",
        "zone": 10,
        "description": "Halifax Road",
        "postcode": "CB23 6AX"
    },
    "CB236AZ": {
        "area": "Great Cambourne",
        "zone": 20,
        "description": "Jeavons Lane",
        "postcode": "CB23 6AZ"
    },
    "CB236BA": {
        "area": "Great Cambourne",
        "zone": 20,
        "description": "Chervil Way",
        "postcode": "CB23 6BA"
    },
    "CB236BB": {
        "area": "Great Cambourne",
        "zone": 20,
        "description": "Goldcrest Court",
        "postcode": "CB23 6BB"
    },
    "CB236BD": {
        "area": "Great Cambourne",
        "zone": 20,
        "description": "Chaffinch Walk",
        "postcode": "CB23 6BD"
    },
    "CB236BE": {
        "area": "Great Cambourne",
        "zone": 20,
        "description": "Woodland Walk",
        "postcode": "CB23 6BE"
    },
    "CB236BF": {
        "area": "Great Cambourne",
        "zone": 20,
        "description": "Cressbrook Drive",
        "postcode": "CB23 6BF"
    },
    "CB236BG": {
        "area": "Great Cambourne",
        "zone": 20,
        "description": "Bullrush Lane",
        "postcode": "CB23 6BG"
    },
    "CB236BH": {
        "area": "Upper Cambourne",
        "zone": 10,
        "description": "Wellington Road",
        "postcode": "CB23 6BH"
    },
    "CB236BJ": {
        "area": "Great Cambourne",
        "zone": 20,
        "description": "High Street",
        "postcode": "CB23 6BJ"
    },
    "CB236BL": {
        "area": "Upper Cambourne",
        "zone": 10,
        "description": "Sterling Way",
        "postcode": "CB23 6BL"
    },
    "CB236BN": {
        "area": "Upper Cambourne",
        "zone": 10,
        "description": "De Havilland Close",
        "postcode": "CB23 6BN"
    },
    "CB236BP": {
        "area": "Upper Cambourne",
        "zone": 10,
        "description": "De Havilland Close",
        "postcode": "CB23 6BP"
    },
    "CB236BQ": {
        "area": "Upper Cambourne",
        "zone": 10,
        "description": "Hurricane Way",
        "postcode": "CB23 6BQ"
    },
    "CB236BS": {
        "area": "Lower Cambourne",
        "zone": 30,
        "description": "Swansley Lane",
        "postcode": "CB23 6BS"
    },
    "CB236BT": {
        "area": "Lower Cambourne",
        "zone": 30,
        "description": "Swansley Lane",
        "postcode": "CB23 6BT"
    },
    "CB236BU": {
        "area": "Lower Cambourne",
        "zone": 30,
        "description": "Wattle Close",
        "postcode": "CB23 6BU"
    },
    "CB236BW": {
        "area": "Great Cambourne",
        "zone": 20,
        "description": "Belfry, The Cambridge Belfry",
        "postcode": "CB23 6BW"
    },
    "CB236BX": {
        "area": "Lower Cambourne",
        "zone": 30,
        "description": "Woolthwaite Lane",
        "postcode": "CB23 6BX"
    },
    "CB236BY": {
        "area": "Lower Cambourne",
        "zone": 30,
        "description": "Arbour Close",
        "postcode": "CB23 6BY"
    },
    "CB236BZ": {
        "area": "Lower Cambourne",
        "zone": 30,
        "description": "Shearling Drive",
        "postcode": "CB23 6BZ"
    },
    "CB236DB": {
        "area": "Lower Cambourne",
        "zone": 30,
        "description": "Akerly Drive",
        "postcode": "CB23 6DB"
    },
    "CB236DD": {
        "area": "Lower Cambourne",
        "zone": 30,
        "description": "Quidditch Lane",
        "postcode": "CB23 6DD"
    },
    "CB236DE": {
        "area": "Lower Cambourne",
        "zone": 30,
        "description": "Apley Way",
        "postcode": "CB23 6DE"
    },
    "CB236DF": {
        "area": "Lower Cambourne",
        "zone": 30,
        "description": "Apley Way",
        "postcode": "CB23 6DF"
    },
    "CB236DG": {
        "area": "Upper Cambourne",
        "zone": 10,
        "description": "Hurricane Way",
        "postcode": "CB23 6DG"
    },
    "CB236DH": {
        "area": "Great Cambourne",
        "zone": 20,
        "description": "Broad Street",
        "postcode": "CB23 6DH"
    },
    "CB236DJ": {
        "area": "Upper Cambourne",
        "zone": 10,
        "description": "Anson Road",
        "postcode": "CB23 6DJ"
    },
    "CB236DL": {
        "area": "Lower Cambourne",
        "zone": 30,
        "description": "Auberry Way",
        "postcode": "CB23 6DL"
    },
    "CB236DQ": {
        "area": "Upper Cambourne",
        "zone": 10,
        "description": "Anson Road",
        "postcode": "CB23 6DQ"
    },
    "CB236DR": {
        "area": "Upper Cambourne",
        "zone": 10,
        "description": "Lysander Close",
        "postcode": "CB23 6DR"
    },
    "CB236DS": {
        "area": "Lower Cambourne",
        "zone": 30,
        "description": "Woodfield Lane",
        "postcode": "CB23 6DS"
    },
    "CB236DT": {
        "area": "Lower Cambourne",
        "zone": 30,
        "description": "Woodfield Lane",
        "postcode": "CB23 6DT"
    },
    "CB236DU": {
        "area": "Upper Cambourne",
        "zone": 10,
        "description": "Oxford Way",
        "postcode": "CB23 6DU"
    },
    "CB236DX": {
        "area": "Great Cambourne",
        "zone": 20,
        "description": "De La Warr Way",
        "postcode": "CB23 6DX"
    },
    "CB236DY": {
        "area": "Upper Cambourne",
        "zone": 10,
        "description": "The Vine Primary School",
        "postcode": "CB23 6DY"
    },
    "CB236DZ": {
        "area": "Great Cambourne",
        "zone": 20,
        "description": "Eastgate",
        "postcode": "CB23 6DZ"
    },
    "CB236ED": {
        "area": "Upper Cambourne",
        "zone": 10,
        "description": "Hudson Road",
        "postcode": "CB23 6ED"
    },
    "CB236EE": {
        "area": "Upper Cambourne",
        "zone": 10,
        "description": "Albemarle Road",
        "postcode": "CB23 6EE"
    },
    "CB236EH": {
        "area": "Great Cambourne",
        "zone": 20,
        "description": "Mallard Close",
        "postcode": "CB23 6EH"
    },
    "CB236EJ": {
        "area": "Upper Cambourne",
        "zone": 10,
        "description": "Roe Walk",
        "postcode": "CB23 6EJ"
    },
    "CB236EL": {
        "area": "Great Cambourne",
        "zone": 20,
        "description": "Broad Street",
        "postcode": "CB23 6EL"
    },
    "CB236EN": {
        "area": "Upper Cambourne",
        "zone": 10,
        "description": "Handley Drive",
        "postcode": "CB23 6EN"
    },
    "CB236EP": {
        "area": "Upper Cambourne",
        "zone": 10,
        "description": "Vickers Way",
        "postcode": "CB23 6EP"
    },
    "CB236EQ": {
        "area": "Upper Cambourne",
        "zone": 10,
        "description": "Wallis Walk",
        "postcode": "CB23 6EQ"
    },
    "CB236ER": {
        "area": "Lower Cambourne",
        "zone": 30,
        "description": "Swansley Lane",
        "postcode": "CB23 6ER"
    },
    "CB236ES": {
        "area": "Lower Cambourne",
        "zone": 30,
        "description": "Swansley Lane",
        "postcode": "CB23 6ES"
    },
    "CB236ET": {
        "area": "Lower Cambourne",
        "zone": 30,
        "description": "Meadowsweet Close",
        "postcode": "CB23 6ET"
    },
    "CB236EU": {
        "area": "Lower Cambourne",
        "zone": 30,
        "description": "St. Johns Way",
        "postcode": "CB23 6EU"
    },
    "CB236EW": {
        "area": "Lower Cambourne",
        "zone": 30,
        "description": "Cherry Court",
        "postcode": "CB23 6EW"
    },
    "CB236EX": {
        "area": "Lower Cambourne",
        "zone": 30,
        "description": "Coppice Drive",
        "postcode": "CB23 6EX"
    },
    "CB236EY": {
        "area": "Great Cambourne",
        "zone": 20,
        "description": "Morrison Supermarkets",
        "postcode": "CB23 6EY"
    },
    "CB236FA": {
        "area": "Upper Cambourne",
        "zone": 10,
        "description": "Gloster Drive",
        "postcode": "CB23 6FA"
    },
    "CB236FB": {
        "area": "Lower Cambourne",
        "zone": 30,
        "description": "Woodfield Lane",
        "postcode": "CB23 6FB"
    },
    "CB236FD": {
        "area": "Lower Cambourne",
        "zone": 30,
        "description": "Woodfield Lane",
        "postcode": "CB23 6FD"
    },
    "CB236FE": {
        "area": "Lower Cambourne",
        "zone": 30,
        "description": "Merle Way",
        "postcode": "CB23 6FE"
    },
    "CB236FF": {
        "area": "Lower Cambourne",
        "zone": 30,
        "description": "Codling Walk",
        "postcode": "CB23 6FF"
    },
    "CB236FG": {
        "area": "Lower Cambourne",
        "zone": 30,
        "description": "Spar Close",
        "postcode": "CB23 6FG"
    },
    "CB236FH": {
        "area": "Lower Cambourne",
        "zone": 30,
        "description": "Sweetentree Way",
        "postcode": "CB23 6FH"
    },
    "CB236FJ": {
        "area": "Upper Cambourne",
        "zone": 10,
        "description": "Mosquito Road",
        "postcode": "CB23 6FJ"
    },
    "CB236FL": {
        "area": "Upper Cambourne",
        "zone": 10,
        "description": "Spitfire Road",
        "postcode": "CB23 6FL"
    },
    "CB236FN": {
        "area": "Upper Cambourne",
        "zone": 10,
        "description": "Spitfire Road",
        "postcode": "CB23 6FN"
    },
    "CB236FP": {
        "area": "Upper Cambourne",
        "zone": 10,
        "description": "Beaufort Road",
        "postcode": "CB23 6FP"
    },
    "CB236FQ": {
        "area": "Lower Cambourne",
        "zone": 30,
        "description": "Purley Road",
        "postcode": "CB23 6FQ"
    },
    "CB236FS": {
        "area": "Upper Cambourne",
        "zone": 10,
        "description": "Welkin Way",
        "postcode": "CB23 6FS"
    },
    "CB236FT": {
        "area": "Lower Cambourne",
        "zone": 30,
        "description": "Medlar Lane",
        "postcode": "CB23 6FT"
    },
    "CB236FU": {
        "area": "Upper Cambourne",
        "zone": 10,
        "description": "Westland Close",
        "postcode": "CB23 6FU"
    },
    "CB236FW": {
        "area": "Upper Cambourne",
        "zone": 10,
        "description": "Firefly Road",
        "postcode": "CB23 6FW"
    },
    "CB236FX": {
        "area": "Great Cambourne",
        "zone": 20,
        "description": "Dovecote Avenue",
        "postcode": "CB23 6FX"
    },
    "CB236FY": {
        "area": "Great Cambourne",
        "zone": 20,
        "description": "Back Lane",
        "postcode": "CB23 6FY"
    },
    "CB236FZ": {
        "area": "Great Cambourne",
        "zone": 20,
        "description": "High Street",
        "postcode": "CB23 6FZ"
    },
    "CB236GA": {
        "area": "Great Cambourne",
        "zone": 20,
        "description": "Playsteds Lane",
        "postcode": "CB23 6GA"
    },
    "CB236GB": {
        "area": "Great Cambourne",
        "zone": 20,
        "description": "Osier Way",
        "postcode": "CB23 6GB"
    },
    "CB236GD": {
        "area": "Great Cambourne",
        "zone": 20,
        "description": "New Hall Lane",
        "postcode": "CB23 6GD"
    },
    "CB236GE": {
        "area": "Great Cambourne",
        "zone": 20,
        "description": "New Hall Lane",
        "postcode": "CB23 6GE"
    },
    "CB236GF": {
        "area": "Great Cambourne",
        "zone": 20,
        "description": "Flitmead",
        "postcode": "CB23 6GF"
    },
    "CB236GG": {
        "area": "Great Cambourne",
        "zone": 20,
        "description": "Glebe Lane",
        "postcode": "CB23 6GG"
    },
    "CB236GH": {
        "area": "Great Cambourne",
        "zone": 20,
        "description": "Hazel Lane",
        "postcode": "CB23 6GH"
    },
    "CB236GJ": {
        "area": "Great Cambourne",
        "zone": 20,
        "description": "Snape Yard",
        "postcode": "CB23 6GJ"
    },
    "CB236GL": {
        "area": "Great Cambourne",
        "zone": 20,
        "description": "The Grove",
        "postcode": "CB23 6GL"
    },
    "CB236GN": {
        "area": "Great Cambourne",
        "zone": 20,
        "description": "Monk Drive",
        "postcode": "CB23 6GN"
    },
    "CB236GP": {
        "area": "Great Cambourne",
        "zone": 20,
        "description": "Watergrove Lane",
        "postcode": "CB23 6GP"
    },
    "CB236GQ": {
        "area": "Great Cambourne",
        "zone": 20,
        "description": "Glebe Lane",
        "postcode": "CB23 6GQ"
    },
    "CB236GR": {
        "area": "Great Cambourne",
        "zone": 20,
        "description": "Reed Cottages",
        "postcode": "CB23 6GR"
    },
    "CB236GS": {
        "area": "Great Cambourne",
        "zone": 20,
        "description": "Acorn Lane",
        "postcode": "CB23 6GS"
    },
    "CB236GT": {
        "area": "Great Cambourne",
        "zone": 20,
        "description": "Bluebell Drive",
        "postcode": "CB23 6GT"
    },
    "CB236GU": {
        "area": "Great Cambourne",
        "zone": 20,
        "description": "Brockholme Mews",
        "postcode": "CB23 6GU"
    },
    "CB236GW": {
        "area": "Great Cambourne",
        "zone": 20,
        "description": "High Street",
        "postcode": "CB23 6GW"
    },
    "CB236GX": {
        "area": "Great Cambourne",
        "zone": 20,
        "description": "High Street",
        "postcode": "CB23 6GX"
    },
    "CB236GY": {
        "area": "Lower Cambourne",
        "zone": 30,
        "description": "Barrow Lane",
        "postcode": "CB23 6GY"
    },
    "CB236GZ": {
        "area": "Great Cambourne",
        "zone": 20,
        "description": "Woodpecker Way",
        "postcode": "CB23 6GZ"
    },
    "CB236HA": {
        "area": "Great Cambourne",
        "zone": 20,
        "description": "Lumbley Close",
        "postcode": "CB23 6HA"
    },
    "CB236HB": {
        "area": "Great Cambourne",
        "zone": 20,
        "description": "Sackville Way",
        "postcode": "CB23 6HB"
    },
    "CB236HE": {
        "area": "Great Cambourne",
        "zone": 20,
        "description": "Broad Street",
        "postcode": "CB23 6HE"
    },
    "CB236HF": {
        "area": "Great Cambourne",
        "zone": 20,
        "description": "Broad Street",
        "postcode": "CB23 6HF"
    },
    "CB236HG": {
        "area": "Great Cambourne",
        "zone": 20,
        "description": "Broad Street",
        "postcode": "CB23 6HG"
    },
    "CB236HH": {
        "area": "Great Cambourne",
        "zone": 20,
        "description": "Broad Street",
        "postcode": "CB23 6HH"
    },
    "CB236HJ": {
        "area": "Great Cambourne",
        "zone": 20,
        "description": "Broad Street",
        "postcode": "CB23 6HJ"
    },
    "CB236HL": {
        "area": "Great Cambourne",
        "zone": 20,
        "description": "Sackville Way",
        "postcode": "CB23 6HL"
    },
    "CB236HN": {
        "area": "Great Cambourne",
        "zone": 20,
        "description": "Bramble Court",
        "postcode": "CB23 6HN"
    },
    "CB236HP": {
        "area": "Great Cambourne",
        "zone": 20,
        "description": "Butler Walk",
        "postcode": "CB23 6HP"
    },
    "CB236HQ": {
        "area": "Great Cambourne",
        "zone": 20,
        "description": "Squire Drive",
        "postcode": "CB23 6HQ"
    },
    "CB236HR": {
        "area": "Great Cambourne",
        "zone": 20,
        "description": "Smithy Way",
        "postcode": "CB23 6HR"
    },
    "CB236HS": {
        "area": "Upper Cambourne",
        "zone": 10,
        "description": "Whitley Road",
        "postcode": "CB23 6HS"
    },
    "CB236HT": {
        "area": "Upper Cambourne",
        "zone": 10,
        "description": "Brace Dein",
        "postcode": "CB23 6HT"
    },
    "CB236HU": {
        "area": "Upper Cambourne",
        "zone": 10,
        "description": "Siskin Road",
        "postcode": "CB23 6HU"
    },
    "CB236HW": {
        "area": "Upper Cambourne",
        "zone": 10,
        "description": "Tempest Road",
        "postcode": "CB23 6HW"
    },
    "CB236HX": {
        "area": "Upper Cambourne",
        "zone": 10,
        "description": "Ventura Avenue",
        "postcode": "CB23 6HX"
    },
    "CB236HY": {
        "area": "Upper Cambourne",
        "zone": 10,
        "description": "Mustang Drive",
        "postcode": "CB23 6HY"
    },
    "CB236HZ": {
        "area": "Upper Cambourne",
        "zone": 10,
        "description": "Botha Close",
        "postcode": "CB23 6HZ"
    },
    "CB236JA": {
        "area": "Upper Cambourne",
        "zone": 10,
        "description": "Bisley Crescent",
        "postcode": "CB23 6JA"
    },
    "CB236JB": {
        "area": "Upper Cambourne",
        "zone": 10,
        "description": "Sunderland Close",
        "postcode": "CB23 6JB"
    },
    "CB236JD": {
        "area": "Upper Cambourne",
        "zone": 10,
        "description": "Otter Road",
        "postcode": "CB23 6JD"
    },
    "CB236JE": {
        "area": "Upper Cambourne",
        "zone": 10,
        "description": "Bristol Drive",
        "postcode": "CB23 6JE"
    },
    "CB236JF": {
        "area": "Upper Cambourne",
        "zone": 10,
        "description": "York Drive",
        "postcode": "CB23 6JF"
    },
    "CB236JG": {
        "area": "Upper Cambourne",
        "zone": 10,
        "description": "Hind Close",
        "postcode": "CB23 6JG"
    },
    "CB236JH": {
        "area": "Upper Cambourne",
        "zone": 10,
        "description": "Albacore Road",
        "postcode": "CB23 6JH"
    },
    "CB236JJ": {
        "area": "Upper Cambourne",
        "zone": 10,
        "description": "Beaufighter Drive",
        "postcode": "CB23 6JJ"
    },
    "CB236JL": {
        "area": "Great Cambourne",
        "zone": 20,
        "description": "Back Lane",
        "postcode": "CB23 6JL"
    },
    "CB236JN": {
        "area": "Great Cambourne",
        "zone": 20,
        "description": "Broad Street",
        "postcode": "CB23 6JN"
    },
    "CB236JP": {
        "area": "Great Cambourne",
        "zone": 20,
        "description": "Drove Gardens",
        "postcode": "CB23 6JP"
    },
    "CB236JQ": {
        "area": "Upper Cambourne",
        "zone": 10,
        "description": "Gladiator Road",
        "postcode": "CB23 6JQ"
    },
    "CB236JR": {
        "area": "Upper Cambourne",
        "zone": 10,
        "description": "Typhoon Way",
        "postcode": "CB23 6JR"
    },
    "CB236JS": {
        "area": "Upper Cambourne",
        "zone": 10,
        "description": "Whirlwind Close",
        "postcode": "CB23 6JS"
    },
    "CB236JT": {
        "area": "Upper Cambourne",
        "zone": 10,
        "description": "Hawkers Close",
        "postcode": "CB23 6JT"
    },
    "CB236JU": {
        "area": "Upper Cambourne",
        "zone": 10,
        "description": "Short Drive",
        "postcode": "CB23 6JU"
    },
    "CB236JW": {
        "area": "Upper Cambourne",
        "zone": 10,
        "description": "Fairey Close",
        "postcode": "CB23 6JW"
    },
    "CB236JX": {
        "area": "Great Cambourne",
        "zone": 20,
        "description": "High Street",
        "postcode": "CB23 6JX"
    },
    "CB236JY": {
        "area": "Upper Cambourne",
        "zone": 10,
        "description": "Gladiator Road",
        "postcode": "CB23 6JY"
    },
    "CB236JZ": {
        "area": "Upper Cambourne",
        "zone": 10,
        "description": "Gladiator Road",
        "postcode": "CB23 6JZ"
    },
    "CB236LA": {
        "area": "Great Cambourne",
        "zone": 20,
        "description": "Back Lane",
        "postcode": "CB23 6LA"
    },
    "CB236LB": {
        "area": "Upper Cambourne",
        "zone": 10,
        "description": "Whittle Way",
        "postcode": "CB23 6LB"
    },
    "CB236LD": {
        "area": "Upper Cambourne",
        "zone": 10,
        "description": "Blackbird Close",
        "postcode": "CB23 6LD"
    },
    "CB236LE": {
        "area": "Upper Cambourne",
        "zone": 10,
        "description": "Gauntlet Drive",
        "postcode": "CB23 6LE"
    },
    "CB236LF": {
        "area": "Upper Cambourne",
        "zone": 10,
        "description": "Battle Close",
        "postcode": "CB23 6LF"
    },
    "CB236LG": {
        "area": "Upper Cambourne",
        "zone": 10,
        "description": "Swordfish Drive",
        "postcode": "CB23 6LG"
    },
    "CB236LH": {
        "area": "Great Cambourne",
        "zone": 20,
        "description": "High Street",
        "postcode": "CB23 6LH"
    },
    "CB236LJ": {
        "area": "Great Cambourne",
        "zone": 20,
        "description": "Back Lane",
        "postcode": "CB23 6LJ"
    }
};
